import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphRequests,
} from '@models/membership/MembershipAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { Card, Spin, theme, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import CustomTooltip from '../common/CustomTooltip'

const { Text } = Typography

const RequestBarChart = () => {
  const { token } = theme.useToken()
  const { selectedDates, selectedMembershipPrograms } = useAnalytics()

  const [chartData, setChartData] = useState<
    MembershipAnalyticsGraphRequests[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchData = async () => {
    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData([])
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'requests',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      programIds: selectedMembershipPrograms,
    }

    try {
      const { data } =
        await fetchAnalyticsGraph<MembershipAnalyticsGraphRequests[]>(
          fetchParams
        )
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedDates, selectedMembershipPrograms])

  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, value } = props
    return value > 0 ? (
      <g>
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {value}
        </text>
      </g>
    ) : null
  }

  interface CustomizedAxisTickProps {
    x: number
    y: number
    stroke?: string
    payload: {
      value: string
    }
  }
  const CustomizedAxisTick: React.FC<CustomizedAxisTickProps> = ({
    x,
    y,
    payload,
  }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={20} y={0} dy={16} textAnchor="end" fill="#bbb">
          {dayjs(payload.value).format('DD MMM')}
        </text>
      </g>
    )
  }

  return (
    <Card
      style={{ alignContent: 'center', textAlign: 'center', minHeight: 400 }}
    >
      {isLoading && <Spin />}
      {error && <Text>{error}</Text>}
      {chartData && !isLoading && !error && (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <YAxis
              dataKey="opening"
              tickLine={false}
              stroke={'#bbb'}
              width={25}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              stroke={'#bbb'}
              height={20}
              tick={(props) => <CustomizedAxisTick {...props} />}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  type="bar"
                  {...props}
                  customLabelFormatter={(payload) => {
                    return dayjs(payload.date).format('D MMM YY')
                  }}
                />
              )}
            />
            <Bar dataKey="opening" fill={token.colorPrimary}>
              <LabelList
                dataKey="opening"
                position="inside"
                content={renderCustomizedLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}

export default RequestBarChart
